import { safeParseLessonContent, type BlockType, type LessonContent } from "@/lib/lesson-content/schema";
import type { EditorBlock, EditorSection } from "./blockDefaults";

/**
 * Import LLM-generated lesson JSON (see docs/ile-authoring/).
 *
 * The input is untrusted: we tolerate a stray ```json fence / surrounding prose,
 * we never trust the model's UUIDs (regenerate every section + block id so they're
 * always valid and unique), and we validate strictly against the schema before
 * handing anything on. `importLessonJson` returns editor-shaped sections for the
 * canvas; `parseLessonEnvelope` returns the raw validated envelope for inserting a
 * lesson straight into the DB. Both share the same normalize core.
 */

/** Content blocks don't gate; everything else does (mirrors makeBlock). */
const CONTENT_TYPES = new Set<BlockType>(["rich_text", "image"]);

export type ImportResult =
  | {
      ok: true;
      sections: EditorSection[];
      intro?: LessonContent["intro"];
      summary: { sections: number; blocks: number };
    }
  | { ok: false; errors: string[] };

export type EnvelopeResult =
  | { ok: true; content: LessonContent; summary: { sections: number; blocks: number } }
  | { ok: false; errors: string[] };

/**
 * Shared core: strip fence/prose → regenerate every section + block UUID (inner
 * element ids inside `data` are left as-is) → validate. Returns the normalized,
 * defaults-filled envelope or a readable list of problems.
 */
function normalizeToEnvelope(
  raw: string,
): { ok: true; content: LessonContent } | { ok: false; errors: string[] } {
  const cleaned = stripFenceAndProse(raw);
  if (!cleaned) return { ok: false, errors: ["Paste the lesson JSON first."] };

  let parsed: unknown;
  try {
    parsed = JSON.parse(cleaned);
  } catch (e) {
    return { ok: false, errors: [`Not valid JSON — ${(e as Error).message}`] };
  }

  const sections = extractSections(parsed);
  if (!Array.isArray(sections)) {
    return {
      ok: false,
      errors: ['Could not find a "sections" array. Expected { "version": 1, "format": "blocks", "sections": [...] }.'],
    };
  }

  const reIded = sections.map((s) => {
    const sec = (s ?? {}) as Record<string, unknown>;
    const blocks = Array.isArray(sec.blocks) ? sec.blocks : [];
    return {
      ...sec,
      id: crypto.randomUUID(),
      blocks: blocks.map((b) => ({ ...((b ?? {}) as Record<string, unknown>), id: crypto.randomUUID() })),
    };
  });

  const intro =
    parsed && typeof parsed === "object" && !Array.isArray(parsed) ? (parsed as { intro?: unknown }).intro : undefined;
  const result = safeParseLessonContent({
    version: 1,
    format: "blocks",
    sections: reIded,
    ...(intro === undefined ? {} : { intro }),
  });
  if (!result.success) return { ok: false, errors: result.error.issues.map(formatIssue) };
  return { ok: true, content: result.data };
}

function summarize(content: LessonContent): { sections: number; blocks: number } {
  const blocks = content.sections.reduce((n, s) => n + s.blocks.length, 0);
  return { sections: content.sections.length, blocks };
}

/** Import onto the editor canvas (returns editor-shaped sections for review + Save). */
export function importLessonJson(raw: string): ImportResult {
  const res = normalizeToEnvelope(raw);
  if (!res.ok) return res;

  const editorSections: EditorSection[] = res.content.sections.map((s) => ({
    id: s.id,
    title: s.title ?? "",
    required_to_advance: s.required_to_advance,
    blocks: s.blocks.map(
      (b): EditorBlock => ({
        id: b.id,
        type: b.type,
        personalized: b.personalized,
        required_to_advance: !CONTENT_TYPES.has(b.type),
        data: b.data as Record<string, unknown>,
      }),
    ),
  }));
  return { ok: true, sections: editorSections, intro: res.content.intro, summary: summarize(res.content) };
}

/**
 * Validate + normalize to the raw envelope — for creating a lesson directly from
 * JSON (the content is POSTed and stored as-is; the server re-validates it).
 */
export function parseLessonEnvelope(raw: string): EnvelopeResult {
  const res = normalizeToEnvelope(raw);
  if (!res.ok) return res;
  return { ok: true, content: res.content, summary: summarize(res.content) };
}

/** Strip a wrapping ```json fence, or slice out the first {...} if the model added prose. */
function stripFenceAndProse(raw: string): string {
  const s = raw.trim();
  if (!s) return "";
  const fence = s.match(/^```[a-zA-Z]*\s*\n([\s\S]*?)\n```$/);
  if (fence) return fence[1].trim();
  if (!s.startsWith("{") && !s.startsWith("[")) {
    const first = s.indexOf("{");
    const last = s.lastIndexOf("}");
    if (first !== -1 && last > first) return s.slice(first, last + 1);
  }
  return s;
}

function extractSections(parsed: unknown): unknown {
  if (Array.isArray(parsed)) return parsed;
  if (parsed && typeof parsed === "object" && "sections" in parsed) {
    return (parsed as { sections: unknown }).sections;
  }
  return null;
}

/** Turn a Zod issue path into a friendly "Section 2 › Block 1 › data › correct_index" string. */
function formatIssue(issue: { path: (string | number)[]; message: string }): string {
  const parts: string[] = [];
  const p = issue.path;
  for (let i = 0; i < p.length; i++) {
    if (p[i] === "sections" && typeof p[i + 1] === "number") {
      parts.push(`Section ${(p[i + 1] as number) + 1}`);
      i++;
    } else if (p[i] === "blocks" && typeof p[i + 1] === "number") {
      parts.push(`Block ${(p[i + 1] as number) + 1}`);
      i++;
    } else {
      parts.push(String(p[i]));
    }
  }
  const where = parts.length ? parts.join(" › ") : "(root)";
  return `${where}: ${issue.message}`;
}
